import { FC, Suspense, useEffect } from "react";
import { graphql, useRelayEnvironment } from "react-relay";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchQuery } from "relay-runtime";

import { PopupImportPageContainerActiveJobQuery } from "~/src/__relay_artifacts__/PopupImportPageContainerActiveJobQuery.graphql";
import { PopupImportContainer } from "~/src/components/features/popup/PopupImport";
import type { PopupImportRowValues } from "~/src/components/features/popup/PopupImport/validationSchema";
import { usePopupImportPageParams } from "~/src/routes/hooks";

import { PopupImportPage } from "./presentations";

const activeJobQuery = graphql`
  query PopupImportPageContainerActiveJobQuery {
    activePopupImportJob {
      slug
      status
    }
  }
`;

type LocationState = {
  popups?: PopupImportRowValues[];
  csvMaxImageSlot?: number;
};

export const PopupImportPageContainer: FC = () => {
  const { siteSlug } = usePopupImportPageParams();
  const location = useLocation();
  const navigate = useNavigate();
  const environment = useRelayEnvironment();
  const state = location.state as LocationState | null | undefined;
  const initialPopups = state?.popups;
  const initialCsvMaxImageSlot = state?.csvMaxImageSlot;

  useEffect(() => {
    let cancelled = false;
    void fetchQuery<PopupImportPageContainerActiveJobQuery>(
      environment,
      activeJobQuery,
      {},
      { fetchPolicy: "network-only" }
    )
      .toPromise()
      .then((data) => {
        if (cancelled) return;
        const job = data?.activePopupImportJob;
        if (!job) return;
        const isActive = job.status === "QUEUED" || job.status === "RUNNING";
        if (isActive) {
          navigate(`/sites/${siteSlug}/popup_groups`, {
            replace: true,
            state: { importJobSlug: job.slug },
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [environment, navigate, siteSlug]);

  return (
    <PopupImportPage siteSlug={siteSlug}>
      <Suspense fallback={null}>
        <PopupImportContainer
          siteSlug={siteSlug}
          initialPopups={initialPopups}
          initialCsvMaxImageSlot={initialCsvMaxImageSlot}
        />
      </Suspense>
    </PopupImportPage>
  );
};
